/**
 * Reservoir — Traffic Analytics Dashboard (admin-only).
 *
 * Route: /admin/traffic
 *
 * Sections:
 *   1. Window picker (24h / 7d / 30d / All)
 *   2. KPI cards (pageviews, visitors, signups, visitor→signup %)
 *   3. Funnel visualization with horizontal bars + drop-off %
 *   4. Top sources table (utm_source → sessions, signups, conv %)
 *   5. Top campaigns table
 *   6. Top pages table
 *
 * Visuals: same design tokens as the rest of the app (Manrope, cream,
 * 14px cards, tabular numerics, emerald accents).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, BarChart3, Eye, MousePointer, UserPlus, Users } from "lucide-react";
import { api } from "@/lib/api";

const WINDOWS = [
  { code: "24h", label: "Last 24h" },
  { code: "7d",  label: "Last 7 days" },
  { code: "30d", label: "Last 30 days" },
  { code: "all", label: "All time" },
];

export default function TrafficAnalyticsDashboard() {
  const [window, setWindow] = useState("7d");
  const [data, setData]     = useState(null);
  const [err,  setErr]      = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: r } = await api.get(`/analytics/dashboard?window=${window}`);
        if (alive) setData(r);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [window]);

  return (
    <div className="min-h-screen" style={{ background: "#fafaf7" }} data-testid="traffic-analytics-page">
      <header className="border-b border-black/5 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Link to="/" className="w-8 h-8 rounded-lg bg-black flex items-center justify-center text-white font-bold font-display text-sm">R</Link>
            <span className="font-display font-semibold tracking-tight">Reservoir <span className="text-neutral-400 font-medium">AI · Traffic</span></span>
          </div>
          <Link to="/admin" className="text-[12.5px] text-neutral-500 hover:text-black">← Admin home</Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        {/* Title + window picker */}
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="section-eyebrow">Internal · traffic + funnel</div>
            <h1 className="font-display font-extrabold text-[36px] md:text-[44px] tracking-tighter leading-tight mt-1.5">
              Analytics
            </h1>
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-[10px] bg-black/[0.04]" role="tablist">
            {WINDOWS.map((w) => (
              <button
                key={w.code}
                onClick={() => setWindow(w.code)}
                className={`px-3.5 py-1.5 text-[12.5px] font-semibold rounded-md transition-colors ${
                  window === w.code ? "bg-white text-black shadow-sm" : "text-neutral-600 hover:text-black"
                }`}
                data-testid={`window-${w.code}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {err ? (
          <ErrorCard message={err} />
        ) : loading || !data ? (
          <LoadingSkeleton />
        ) : (
          <DashboardBody data={data} />
        )}
      </main>
    </div>
  );
}

// ----------- Body -------------------------------------------------------

function DashboardBody({ data }) {
  const { kpis, funnel, top_sources, top_campaigns, top_pages } = data;
  return (
    <>
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard Icon={Eye}         label="Page views"       value={kpis.pageviews}                  />
        <KpiCard Icon={Users}       label="Unique visitors"  value={kpis.unique_visitors}            />
        <KpiCard Icon={UserPlus}    label="Waitlist signups" value={kpis.signups}                    />
        <KpiCard Icon={MousePointer}label="Visitor → signup" value={kpis.visitor_to_signup_pct + "%"} unit="" />
      </div>

      {/* Funnel */}
      <Section title="Conversion funnel" Icon={ArrowDown} testid="funnel">
        <Funnel stages={funnel} />
      </Section>

      {/* Top sources */}
      <Section title="Top sources (UTM source)" Icon={BarChart3} testid="top-sources">
        <SourcesTable rows={top_sources} />
      </Section>

      {/* Top campaigns */}
      {top_campaigns?.length > 0 && (
        <Section title="Top campaigns" Icon={BarChart3} testid="top-campaigns">
          <CampaignsTable rows={top_campaigns} />
        </Section>
      )}

      {/* Top pages */}
      <Section title="Top pages" Icon={BarChart3} testid="top-pages">
        <PagesTable rows={top_pages} />
      </Section>
    </>
  );
}

// ----------- Sub-components --------------------------------------------

function Section({ title, Icon, children, testid }) {
  return (
    <section className="mt-10" data-testid={`section-${testid}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={13} className="text-neutral-500" />}
        <div className="section-eyebrow">{title}</div>
      </div>
      <div className="card bg-white rounded-[14px] border border-black/8 p-6">
        {children}
      </div>
    </section>
  );
}

function KpiCard({ Icon, label, value, unit }) {
  return (
    <div className="card bg-white rounded-[14px] border border-black/8 p-5">
      <div className="flex items-center gap-2 text-neutral-500">
        {Icon && <Icon size={13} />}
        <span className="text-[11px] uppercase tracking-[0.16em] font-semibold">{label}</span>
      </div>
      <div className="font-display font-extrabold tracking-tighter text-[34px] mt-2 leading-none tabular">
        {typeof value === "number" ? value.toLocaleString() : value}
        {unit ? <span className="text-[16px] text-neutral-500 font-bold ml-1">{unit}</span> : null}
      </div>
    </div>
  );
}

function Funnel({ stages }) {
  const max = useMemo(
    () => Math.max(1, ...(stages || []).map((s) => s.count || 0)),
    [stages]
  );
  return (
    <div className="space-y-3.5" data-testid="funnel-bars">
      {(stages || []).map((s) => {
        const width = max ? Math.max(2, Math.round((100 * (s.count || 0)) / max)) : 2;
        return (
          <div key={s.stage} className="grid grid-cols-[160px_1fr_auto_80px] gap-3 items-center text-[13px]">
            <div className="font-semibold text-neutral-800 truncate">{s.stage}</div>
            <div className="h-7 bg-black/[0.04] rounded-md overflow-hidden">
              <div className="h-full bg-black rounded-md transition-all duration-700" style={{ width: `${width}%` }} />
            </div>
            <div className="tabular font-display font-extrabold text-[15px]">{(s.count || 0).toLocaleString()}</div>
            <div className="tabular text-[12px] text-neutral-500 text-right">
              {s.pct_of_prev != null ? `${s.pct_of_prev}% ↓` : "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SourcesTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <EmptyRow>No traffic with UTM data yet.</EmptyRow>;
  }
  return (
    <table className="w-full text-[13px]" data-testid="sources-table">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">
          <th className="py-2 pr-3">Source</th>
          <th className="py-2 pr-3 text-right">Sessions</th>
          <th className="py-2 pr-3 text-right">Pageviews</th>
          <th className="py-2 pr-3 text-right">Signups</th>
          <th className="py-2 text-right">Conv %</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.source} className="border-t border-black/5">
            <td className="py-2.5 pr-3 font-medium">{r.source}</td>
            <td className="py-2.5 pr-3 text-right tabular">{r.sessions.toLocaleString()}</td>
            <td className="py-2.5 pr-3 text-right tabular text-neutral-600">{r.pageviews.toLocaleString()}</td>
            <td className="py-2.5 pr-3 text-right tabular">{r.signups ?? "—"}</td>
            <td className="py-2.5 text-right tabular">
              {r.conversion_pct > 0
                ? <span className="text-emerald-700 font-semibold">{r.conversion_pct}%</span>
                : <span className="text-neutral-400">0%</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CampaignsTable({ rows }) {
  return (
    <table className="w-full text-[13px]" data-testid="campaigns-table">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">
          <th className="py-2 pr-3">Campaign</th>
          <th className="py-2 pr-3">Source</th>
          <th className="py-2 pr-3">Content</th>
          <th className="py-2 pr-3 text-right">Sessions</th>
          <th className="py-2 text-right">Pageviews</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-black/5">
            <td className="py-2.5 pr-3 font-medium">{r.campaign}</td>
            <td className="py-2.5 pr-3 text-neutral-600">{r.source}</td>
            <td className="py-2.5 pr-3 text-neutral-600">{r.content}</td>
            <td className="py-2.5 pr-3 text-right tabular">{r.sessions.toLocaleString()}</td>
            <td className="py-2.5 text-right tabular text-neutral-600">{r.pageviews.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PagesTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <EmptyRow>No pageviews recorded yet.</EmptyRow>;
  }
  return (
    <table className="w-full text-[13px]" data-testid="pages-table">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">
          <th className="py-2 pr-3">Path</th>
          <th className="py-2 pr-3 text-right">Sessions</th>
          <th className="py-2 text-right">Pageviews</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.path} className="border-t border-black/5">
            <td className="py-2.5 pr-3 font-mono text-[12px] truncate max-w-[600px]">{r.path}</td>
            <td className="py-2.5 pr-3 text-right tabular">{r.sessions.toLocaleString()}</td>
            <td className="py-2.5 text-right tabular text-neutral-600">{r.pageviews.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmptyRow({ children }) {
  return <div className="text-[13px] text-neutral-500 py-3">{children}</div>;
}

function ErrorCard({ message }) {
  return (
    <div className="card bg-red-50 border border-red-200 rounded-[14px] p-5">
      <div className="font-display font-bold text-red-800">Couldn't load analytics</div>
      <div className="text-[13px] text-red-700 mt-1">{message}</div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="card bg-white rounded-[14px] border border-black/8 p-5 h-[110px] animate-pulse" />
        ))}
      </div>
      <div className="card bg-white rounded-[14px] border border-black/8 p-6 h-[280px] animate-pulse" />
    </div>
  );
}
